import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import loginBannerImg from '../../assets/loginbannerimg.svg';
import PoweredBySvg from '../../assets/poweredBy.svg';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const API_BASE_URL = process.env.REACT_APP_BASEURL;
    console.log('API_BASE_URL:', API_BASE_URL);

    // Validation function for email or mobile number
    const validateEmailOrPhone = (value) => {
        if (!value) {
            return 'Email or mobile number is required';
        }

        // Email validation regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        
        // Mobile number validation - exactly 10 digits, or starts with + followed by country code and number
        // Remove spaces and dashes for phone validation
        const cleanedValue = value.replace(/[\s-]/g, '');
        
        // Check for exactly 10 digits (without +)
        const tenDigitPhoneRegex = /^[0-9]{10}$/;
        
        // Check for international format: + followed by country code (1-3 digits) and phone number (7-12 digits)
        const internationalPhoneRegex = /^\+[1-9]\d{6,14}$/;
        
        if (emailRegex.test(value)) {
            return ''; // Valid email
        } else if (tenDigitPhoneRegex.test(cleanedValue)) {
            return ''; // Valid 10-digit phone
        } else if (internationalPhoneRegex.test(cleanedValue)) {
            return ''; // Valid international phone
        } else {
            // Show error if input exists but doesn't match any valid format
            return 'Please enter a valid email address or mobile number';
        }
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        // Validate in real-time as user types
        if (value.length > 0) {
            const validationError = validateEmailOrPhone(value);
            setEmailError(validationError);
        } else {
            // Clear error if field is empty
            setEmailError('');
        }
    };

    const handleEmailBlur = () => {
        // Validate on blur as well
        const validationError = validateEmailOrPhone(email);
        setEmailError(validationError);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        // Validate email/mobile number
        const emailValidationError = validateEmailOrPhone(email);
        if (emailValidationError) {
            setEmailError(emailValidationError);
            setIsLoading(false);
            return;
        }

        try {
            // Make API call to login endpoint
            const response = await axios.post(`${API_BASE_URL}/auth/login`, {
                email_or_phone: email,
                password: password
            }, {
                headers: {
                    'accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            // Check if login was successful
            if (response.status === 200 && response.data.access_token) {
                const { access_token, token_type, user_id, name } = response.data;

                // Store authentication data in localStorage
                localStorage.setItem('access_token', access_token);
                localStorage.setItem('token_type', token_type);
                localStorage.setItem('user_id', user_id.toString());
                localStorage.setItem('user_name', name);
                localStorage.setItem('user_email', email);
                localStorage.setItem('isAuthenticated', 'true');

                console.log('Login successful:', { name, email, user_id });

                // Navigate to home page
                navigate('/home');
            } else {
                setError('Login failed. Please try again.');
                setIsLoading(false);
            }
        } catch (err) {
            console.error('Login error:', err);

            // Handle different error scenarios
            if (err.response) {
                // Server responded with error status
                if (err.response.status === 401) {
                    setError('Invalid email or password. Please try again.');
                } else if (err.response.status === 400) {
                    setError('Please check your email and password format.');
                } else {
                    setError(err.response.data?.detail || 'Login failed. Please try again.');
                }
            } else if (err.request) {
                // Request was made but no response received
                setError('Unable to connect to server. Please check your internet connection.');
            } else {
                // Something else happened
                setError('An unexpected error occurred. Please try again.');
            }

            setIsLoading(false);
        }
    };

    return (
        <div className="flex h-screen w-screen relative bg-gray-100">
            {/* Left Side - Login Banner Image */}
            <div className="flex-1 w-1/2 h-screen overflow-hidden flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600">
                <img
                    src={loginBannerImg}
                    alt="Login Banner"
                    className="w-full h-full object-cover"
                />
            </div>

            {/* Right Side - Login Form */}
            <div className="flex-1 w-1/2 h-screen flex items-center justify-center p-10 overflow-y-auto" style={{ backgroundColor: '#F3F8FF' }}>
                <div className="w-full max-w-md p-5">
                    {/* Header Section */}
                    <div className="text-center mb-10">
                        <h1 className="text-3xl font-semibold text-gray-900 mb-2">
                            Welcome Back To Helixia
                        </h1>
                        <p className="text-sm text-gray-600">
                            Your AI Pharmaceutical Intelligence Platform
                        </p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-600 text-center">{error}</p>
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                        {/* Email/Mobile Input */}
                        <div className="flex flex-col gap-2">
                            <label htmlFor="email" className="text-sm font-medium text-gray-700">
                                Email/Mobile number
                            </label>
                            <input
                                type="text"
                                id="email"
                                placeholder="Enter email/mobile number"
                                value={email}
                                onChange={handleEmailChange}
                                onBlur={handleEmailBlur}
                                required
                                className={`w-full px-4 py-3 border rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#004171] focus:border-transparent transition-all ${
                                    emailError ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                                }`}
                            />
                            {emailError && (
                                <p className="text-sm text-red-600 mt-1">{emailError}</p>
                            )}
                        </div>

                        {/* Password Input */}
                        <div className="flex flex-col gap-2">
                            <label htmlFor="password" className="text-sm font-medium text-gray-700">
                                Password
                            </label>
                            <div className="relative w-full">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    placeholder="Enter email/mobile number"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#004171] focus:border-transparent transition-all"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 transition-colors"
                                    aria-label="Toggle password visibility"
                                >
                                    {showPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 5C5.63636 5 2 12 2 12C2 12 5.63636 19 12 19C18.3636 19 22 12 22 12C22 12 18.3636 5 12 5Z" stroke="currentColor" strokeWidth="2" />
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                            <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth="2" />
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 5C5.63636 5 2 12 2 12C2 12 5.63636 19 12 19C18.3636 19 22 12 22 12C22 12 18.3636 5 12 5Z" stroke="currentColor" strokeWidth="2" />
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Login Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full px-6 py-3.5 bg-[#004171] text-white rounded-lg text-base font-semibold transition-all duration-300 mt-2 ${isLoading
                                ? 'opacity-70 cursor-not-allowed'
                                : 'hover:bg-[#003d7a] hover:-translate-y-0.5 hover:shadow-lg'
                                }`}
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Logging in...
                                </span>
                            ) : (
                                'Log In'
                            )}
                        </button>

                        {/* Forgot Password */}
                        {/* <div className="text-center text-sm text-gray-600 mt-4">
                            Forgot password?{' '}
                            <a href="/reset" className="text-blue-600 font-medium hover:text-blue-800 hover:underline transition-colors">
                                Reset
                            </a>
                        </div> */}

                        {/* Sign Up Section */}
                        <div className="text-center mt-6 pt-6 border-t border-gray-300">
                            <p className="text-sm text-gray-600 mb-3">
                                Don't have an account?
                            </p>
                            <button
                                type="button"
                                onClick={() => navigate('/signup')}
                                className="px-8 py-2.5 bg-transparent text-[#004171 border-2 border-[#004171] rounded-lg text-sm font-semibold hover:bg-[#004171] hover:text-white hover:-translate-y-0.5 hover:shadow-md transition-all duration-300"
                            >
                                Sign Up
                            </button>
                        </div>
                    </form>
                    
                    {/* Powered By Section */}
                    {/* <div className="text-center mt-8">
                        <img 
                            src={PoweredBySvg} 
                            alt="Powered By Curebay" 
                            className="mx-auto"
                            style={{ width: "107px", height: "34px" }}
                        />
                    </div> */}
                     <div className="text-center mt-8">
                        <p className="text-base text-[#004171] font-semibold">
                            IntelliMed powered by CureBay
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;

